import {
  chakra,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Text,
  useToast,
  MenuList,
  Menu,
  MenuItem,
  MenuButton,
  Box,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { PublishedStatusBadge } from '../../navbar/PublishedStatusBadge';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useNavigate, useParams } from 'react-router-dom';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  GripVerticalIcon,
  MessagesSquareIcon,
  MoreHorizontalIcon,
  PencilIcon,
  Plus,
  Trash2Icon,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { withPermission } from '../../withPermission';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { initialConversationFlowContent } from '../initialConversationFlowContent';

// Type for learning resources returned from the backend
type LearningResourcesWithRelations = NonNullable<
  RouterOutputs['unitsOfCompetency']['resourcesByIdAndKind']
>['learningResources'][number];

const List = chakra(Reorder.Group);
const ListItem = chakra(Reorder.Item);

export const ConversationalGuidePage = withPermission('canManageCourseContent', () => {
  const { unitId } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [isDeleteConfirmationAlertOpen, setIsDeleteConfirmationAlertOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);

  if (!unitId) {
    throw new Error('unitId is required');
  }
  const utils = trpcAdmin.useUtils();

  const resourcesByIdAndKindInput = {
    id: unitId,
    resourceKind: 'CONVERSATION',
  } as const;

  const { data, isLoading: isDataLoading } =
    trpcAdmin.unitsOfCompetency.resourcesByIdAndKind.useQuery(resourcesByIdAndKindInput);

  // resources is a subsection of data, that can be reordered at runtime
  const [resources, setResources] = useState<any[]>(data?.learningResources || []);

  // if data changes, we may need to update the resources
  // this happens when the data is fetched for the first time
  // and after the data is refetched after reordering
  useMemo(() => {
    if (data?.learningResources) {
      setResources(data.learningResources);
    }
  }, [data]);

  const createResource = trpcAdmin.resources.create.useMutation({
    onSuccess: () => {
      toast({
        title: t`Learning material added successfully.`,
        status: 'success',
        isClosable: true,
      });
    },
    onSettled: (data) => {
      // after creating a resource, navigate to the resource page
      if (data?.id) {
        navigate(`/resources/${data.id}`);
      }
    },
  });

  const archiveResource = trpcAdmin.resources.archive.useMutation();

  const reorderResources = trpcAdmin.unitsOfCompetency.reorderResources.useMutation({
    async onMutate(newOrder) {
      // Cancel outgoing fetches (so they don't overwrite our optimistic update)
      await utils.unitsOfCompetency.resourcesByIdAndKind.cancel();

      // Get the data from the queryCache
      const prevData = utils.unitsOfCompetency.resourcesByIdAndKind.getData();

      // Optimistically update the data with our new order
      utils.unitsOfCompetency.resourcesByIdAndKind.setData(
        resourcesByIdAndKindInput,
        (prevData) => {
          if (!prevData) {
            return prevData;
          }
          return {
            ...prevData,
            learningResources: newOrder.resourceIds.flatMap((resourceId) => {
              const found = prevData.learningResources.find(
                (resource) => resource.resource_id === resourceId,
              );
              return found ? [found] : [];
            }),
          };
        },
      );

      // Return the previous data so we can revert if something goes wrong
      return { prevData };
    },
    onError(_err, _newOrder, ctx) {
      // If the mutation fails, use the context-value from onMutate
      if (ctx) {
        utils.unitsOfCompetency.resourcesByIdAndKind.setData(
          resourcesByIdAndKindInput,
          ctx.prevData,
        );
      }
    },
    onSettled() {
      utils.unitsOfCompetency.resourcesByIdAndKind.invalidate();
    },
  });

  const handleCreateResource = useCallback(() => {
    if (!data) {
      return;
    }
    createResource.mutate({
      title: `${data.code} - Conversational Guide - Section ${data.learningResources.length + 1}`,
      kind: 'CONVERSATION',
      content: initialConversationFlowContent,
      unitOfCompetencyId: data.id,
      position: data.learningResources.length,
    });
  }, [data, createResource]);

  const saveReorder = useCallback(() => {
    if (!data) {
      return;
    }
    reorderResources.mutate({
      unitId: data.id,
      resourceIds: resources.map((resource) => resource.resource_id),
    });
  }, [data, resources, reorderResources]);

  const onClickDeleteResource = useCallback(
    (resourceId: string) => {
      if (!data) {
        return;
      }
      archiveResource.mutate(resourceId, {
        onSuccess: () => {
          // remove the resource from the resources list
          const updatedResources = resources.filter(
            (resource) => resource.resource_id !== resourceId,
          );

          // update the order of the resources
          reorderResources.mutate({
            unitId: data.id,
            resourceIds: updatedResources.map((resource) => resource.resource_id),
          });

          toast({
            title: t`Conversational flow deleted successfully.`,
            status: 'success',
            isClosable: true,
          });
        },
      });
    },
    [archiveResource, data, reorderResources, resources, toast],
  );

  if (isDataLoading) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Container h={'full'} mx={'auto'}>
      <Stack p="16px" spacing="4" w="full" bg="bg.surface">
        <HStack spacing="4" justify="space-between">
          <PageHeading>
            {data?.code}&nbsp;-&nbsp;{data?.title}&nbsp;
            <Trans>Conversational Guide</Trans>
          </PageHeading>
        </HStack>

        {resources.length > 0 ? (
          <List values={resources} onReorder={setResources} listStyleType="none">
            <Stack spacing="4" width="full">
              {resources.map((item: LearningResourcesWithRelations) => (
                <Item
                  key={item.resource_id}
                  item={item}
                  saveReorder={saveReorder}
                  setSelectedResourceId={setSelectedResourceId}
                  setIsDeleteConfirmationAlertOpen={setIsDeleteConfirmationAlertOpen}
                />
              ))}
            </Stack>
          </List>
        ) : null}

        <Flex
          direction="column"
          align="center"
          justify="center"
          h="70px"
          w="full"
          rounded="lg"
          border="2px dashed"
          borderColor="border.emphasis"
          as={Button}
          onClick={handleCreateResource}
          variant="tertiary"
          isLoading={createResource.isPending}
        >
          <Flex gap={2} alignItems="center">
            <Icon as={Plus} boxSize="24px" />
            <Trans>Add new section</Trans>
          </Flex>
        </Flex>
      </Stack>

      <ConfirmationDialog
        isOpen={isDeleteConfirmationAlertOpen}
        onClose={() => setIsDeleteConfirmationAlertOpen(false)}
        title={<Trans>Delete Conversational Flow</Trans>}
        body={
          <Box>
            <Trans>
              You are about to delete this conversational flow. Are you sure you want to continue?
            </Trans>
            {selectedResourceId ? (
              <Box
                mt={4}
                p={4}
                borderColor="danger.subtle"
                bg="bg.danger-subtle"
                borderRadius="8px"
              >
                <Text fontSize="sm" fontWeight="medium">
                  {
                    resources.find((unit) => unit.resource_id === selectedResourceId)?.resource
                      .title
                  }
                </Text>
              </Box>
            ) : null}
          </Box>
        }
        onConfirm={() => {
          if (selectedResourceId) {
            onClickDeleteResource(selectedResourceId);
          }
          setIsDeleteConfirmationAlertOpen(false);
        }}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
      />
    </Container>
  );
});

function Item({
  item,
  saveReorder,
  setSelectedResourceId,
  setIsDeleteConfirmationAlertOpen,
}: {
  item: LearningResourcesWithRelations;
  saveReorder: () => void;
  setSelectedResourceId: (_resourceId: string) => void;
  setIsDeleteConfirmationAlertOpen: (_isOpen: boolean) => void;
}) {
  const dragControls = useDragControls();

  const navigate = useNavigate();

  const editResource = useCallback(() => {
    if (item.resource_id !== null) {
      navigate(`/resources/${item.resource_id}`);
    }
  }, [item, navigate]);

  if (!item.resource) {
    return null;
  }

  return (
    <ListItem
      key={item.resource_id}
      value={item}
      position="relative"
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={saveReorder}
    >
      <HStack gap="4px" align="center">
        <HStack
          p="8px"
          borderWidth="1px"
          borderColor="border.emphasis"
          borderRadius="8px"
          bg="bg.canvas"
          gap="16px"
          w="full"
          justify="start"
        >
          <HStack
            gap="16px"
            align="center"
            w="full"
            flex="1"
            onClick={editResource}
            cursor="pointer"
          >
            <Icon as={MessagesSquareIcon} color="fg.emphasis" boxSize="32px" strokeWidth="1px" />
            <Text fontSize="lg" lineHeight="7" fontWeight="medium" color="fg.default">
              {item.resource.title}
            </Text>
          </HStack>
          <HStack gap="16px">
            <PublishedStatusBadge isPublished={item.resource.last_published_version_id != null} />
            <Menu gutter={12}>
              <MenuButton
                as={IconButton}
                aria-label={t`More options`}
                _hover={{ color: 'brand.600' }}
                _active={{ color: 'brand.600', bg: 'bg.accent.canvas' }}
                icon={<Icon as={MoreHorizontalIcon} />}
                size="sm"
                variant="tertiary"
                onClick={() => setSelectedResourceId(item.resource_id)}
              />
              <MenuList fontSize="md">
                <MenuItem icon={<PencilIcon size="16px" />} onClick={editResource}>
                  {t`Edit`}
                </MenuItem>
                <MenuItem
                  icon={<Trash2Icon size="16px" />}
                  onClick={() => setIsDeleteConfirmationAlertOpen(true)}
                >
                  {t`Delete`}
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </HStack>
        <IconButton
          _hover={{ color: 'brand.600' }}
          icon={<Icon as={GripVerticalIcon} />}
          cursor="grab"
          size="sm"
          variant="tertiary"
          aria-label={t`Reorder learning material`}
          onPointerDown={(e) => dragControls.start(e)}
        />
      </HStack>
    </ListItem>
  );
}
